import React, { useContext } from "react";
import {
  Box,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation,
  PAGE_ROUTE_BLOG
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import {
  PRODUCT_COMPARISON_ROWS,
  PRODUCT_SELLING_MODE_ROWS,
  PRODUCT_WORKFLOW_STEPS
} from "./constants/productVsProductItemContent";
import {
  publicInfoBodySx,
  publicInfoLinkSx,
  publicInfoOrderedListSx,
  publicInfoSectionPanelSx,
  publicInfoSectionStackSx,
  publicInfoSectionTitleSx
} from "./constants/publicInfoPageStyles";

const ProductVsProductItemPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const userGuidePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_USER_GUIDE, language, defaultLang)
      : `/${PAGE_ROUTE_USER_GUIDE}`;
  const contactPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang)
      : `/${PAGE_ROUTE_CONTACT}`;
  const learnMorePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_BLOG, language, defaultLang)
      : `/${PAGE_ROUTE_BLOG}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="products-items-page"
        eyebrow={t("products-items-page-eyebrow", "Learn more")}
        title={t("products-items-page-title", "Products vs. product items")}
        intro={t(
          "products-items-page-intro",
          "Learn the difference between your product catalog and the actual stock available in your stores."
        )}
      />

      <Stack sx={publicInfoSectionStackSx}>
        <PublicInfoCallout testId="products-items-page-analogy">
          {t(
            "products-items-page-analogy",
            "Think of a product as what appears on your menu, and product items as what is available in the kitchen. The product describes what you sell; product items show what you currently have in stock."
          )}
        </PublicInfoCallout>

        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("products-items-page-category-heading", "What is a category?")}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "products-items-page-category-body-1",
              "A category helps you organize related products. For example, you may have categories such as Beverages, Clothing, Electronics, Tools, or Fasteners. Categories make it easier for your team to browse, search, and filter products."
            )}
          </Typography>
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "products-items-page-category-body-2",
              "Before adding products, you need to create at least one category. Every product must belong to a category so your catalog stays organized from the start."
            )}
          </Typography>
        </Box>

        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("products-items-page-product-heading", "What is a product?")}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "products-items-page-product-body-1",
              "A product is the catalog record for something your business sells. It includes information such as the product name, category, brand, image, and how the product is sold, for example by unit, weight, length, or volume."
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "products-items-page-product-body-2",
              "You create one product for each item or service you offer. A product does not track the quantity available on the shelf. Stock quantities are managed through product items."
            )}
          </Typography>
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "products-items-page-product-body-3",
              "When adding a product, you must assign it to a category. You can also link it to a supplier, or create a new supplier from the same form without leaving the page."
            )}
          </Typography>
        </Box>

        <Box>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t(
              "products-items-page-selling-heading",
              "Selling mode, base unit & partial selling"
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "products-items-page-selling-intro-1",
              "When you add a product, you define how it should be sold at checkout. Three settings work together: selling mode, base unit, and partial selling."
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 2 }}>
            {t(
              "products-items-page-selling-intro-2",
              "The selling mode is set at the product level and applies to all product items under that product. The base unit defines how stock and sales are counted. Partial selling controls whether customers can buy part of the available stock instead of the full quantity."
            )}
          </Typography>
          <TableContainer
            sx={{
              borderRadius: 2,
              border: (theme) =>
                `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              overflowX: "auto"
            }}
            data-testid="products-items-page-selling-table"
          >
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, width: "18%" }}>
                    {t(
                      "products-items-page-selling-table-mode-col",
                      "Selling mode"
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "28%" }}>
                    {t(
                      "products-items-page-selling-table-how-col",
                      "How it works"
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "18%" }}>
                    {t(
                      "products-items-page-selling-table-base-unit-col",
                      "Base unit"
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t(
                      "products-items-page-selling-table-partial-col",
                      "Partial selling"
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PRODUCT_SELLING_MODE_ROWS.map((row) => (
                  <TableRow
                    key={row.id}
                    data-testid={`products-items-page-selling-row-${row.id}`}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:nth-of-type(even)": {
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.03)
                      }
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        verticalAlign: "top"
                      }}
                    >
                      {t(row.modeKey, row.modeFallback)}
                    </TableCell>
                    <TableCell
                      sx={{ ...publicInfoBodySx, verticalAlign: "top" }}
                    >
                      {t(row.howKey, row.howFallback)}
                    </TableCell>
                    <TableCell
                      sx={{ ...publicInfoBodySx, verticalAlign: "top" }}
                    >
                      {t(row.baseUnitKey, row.baseUnitFallback)}
                    </TableCell>
                    <TableCell
                      sx={{ ...publicInfoBodySx, verticalAlign: "top" }}
                    >
                      {t(row.partialKey, row.partialFallback)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("products-items-page-item-heading", "What is a product item?")}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "products-items-page-item-body-1",
              "A product item is the actual stock record for a product in a specific store. It tracks inventory details such as available quantity, reserved quantity, sold quantity, variation, SKU, price, and stock status."
            )}
          </Typography>
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "products-items-page-item-body-2",
              "One product can have many product items. For example, the same product may have different sizes, colors, batches, lots, or store locations. When stock is received, sold, reserved, or adjusted, the product item quantities are updated."
            )}
          </Typography>
        </Box>

        <Box>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("products-items-page-table-heading", "Side-by-side comparison")}
          </Typography>
          <TableContainer
            sx={{
              borderRadius: 2,
              border: (theme) =>
                `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              overflowX: "auto"
            }}
            data-testid="products-items-page-table"
          >
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, width: "22%" }}>
                    {t("products-items-page-table-aspect-col", "Aspect")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "39%" }}>
                    {t("products-items-page-table-product-col", "Product")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("products-items-page-table-item-col", "Product item")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PRODUCT_COMPARISON_ROWS.map((row) => (
                  <TableRow
                    key={row.id}
                    data-testid={`products-items-page-row-${row.id}`}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:nth-of-type(even)": {
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.03)
                      }
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        verticalAlign: "top"
                      }}
                    >
                      {t(row.aspectKey, row.aspectFallback)}
                    </TableCell>
                    <TableCell
                      sx={{ ...publicInfoBodySx, verticalAlign: "top" }}
                    >
                      {t(row.productKey, row.productFallback)}
                    </TableCell>
                    <TableCell
                      sx={{ ...publicInfoBodySx, verticalAlign: "top" }}
                    >
                      {t(row.itemKey, row.itemFallback)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("products-items-page-workflow-heading", "Typical setup flow")}
          </Typography>
          <Box component="ol" sx={publicInfoOrderedListSx}>
            {PRODUCT_WORKFLOW_STEPS.map((step) => (
              <Box key={step.id} component="li">
                {t(step.stepKey, step.stepFallback)}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("products-items-page-qr-heading", "QR codes & orders")}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "products-items-page-qr-body-1",
              "Each product item can have a QR code linked to that specific stock record. You can print QR codes when adding stock or from the product item list, then place them on shelves, boxes, or batches."
            )}
          </Typography>
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "products-items-page-qr-body-2",
              "When staff create a sales or purchase order, they can scan the QR code to add the correct item faster and with fewer mistakes."
            )}
          </Typography>
        </Box>

        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t(
              "products-items-page-example-heading",
              "Example: a hardware shop"
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "products-items-page-example-body-1",
              "Imagine you run a hardware shop selling tools, fasteners, cables, and building supplies. You can create categories such as Tools and Fasteners, then add products with different selling settings."
            )}
          </Typography>
          <Box component="ul" sx={{ ...publicInfoBodySx, pl: 2.5, mb: 1.5 }}>
            <Box component="li" sx={{ mb: 1 }}>
              {t(
                "products-items-page-example-quincaillerie-drill",
                '"Cordless drill 18V" — Single item, base unit Unit. Each drill is sold as one complete tool, so partial selling does not apply.'
              )}
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              {t(
                "products-items-page-example-quincaillerie-screws",
                '"Wood screws 4×40 mm" — Unit based, base unit Unit, partial selling allowed. You may stock a box of 500 screws, sell 40 to a customer, and keep tracking the remaining quantity.'
              )}
            </Box>
            <Box component="li">
              {t(
                "products-items-page-example-quincaillerie-cable",
                '"Electrical cable 2.5 mm²" — Measured, base unit Metre, partial selling allowed. You can sell the exact length a customer needs, such as 2.5 m, 10 m, or 25 m.'
              )}
            </Box>
          </Box>
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "products-items-page-example-body-2",
              "After setting up products, you add product items for the stock available in each store. Staff can then scan or select items at checkout. Kivunova FMS applies each product's selling rules and updates quantities when orders are completed."
            )}
          </Typography>
        </Box>

        <PublicInfoCtaPanel
          title={t(
            "products-items-page-cta-title",
            "Ready to set up your catalog?"
          )}
          testId="products-items-page-cta"
        >
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "products-items-page-cta-body",
              "Follow the user guide for step-by-step help with adding categories, products, and stock."
            )}{" "}
            <MuiLink
              component={Link}
              to={userGuidePath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("products-items-page-cta-guide-link", "User guide")}
            </MuiLink>
            {t("products-items-page-cta-mid", " · ")}{" "}
            <MuiLink
              component={Link}
              to={contactPath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("products-items-page-cta-contact-link", "Contact us")}
            </MuiLink>
            {t("products-items-page-cta-mid", " · ")}{" "}
            <MuiLink
              component={Link}
              to={learnMorePath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("products-items-page-cta-learn-more-link", "Learn more")}
            </MuiLink>
          </Typography>
        </PublicInfoCtaPanel>
      </Stack>
    </PublicMarketingLayout>
  );
};

export default ProductVsProductItemPage;
